import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { Radio, DatePicker } from 'antd'
import { createSimpleStore } from 'react-simple-reducer'
import dayjs from 'dayjs'
import Axios from 'axios'
import { ComparativoWidget } from './ComparativoWidget'
import { SemanalTable } from './SemanalTable'
import { SemanalChart } from './SemanalChart'

const { RangePicker } = DatePicker

export interface PeriodoRowModel {
  periodStart: string
  count: number
  valorNewDriverSum: number
  countElapsed: number
  valorNewDriverSumElapsed: number
  previousCount: number | null
  previousValorNewDriverSum: number | null
  isCurrent: boolean
  holidayNames: string[]
  annotation: string | null
  workdays: number | null
}

export type Metric = 'count' | 'valorNewDriverSum'
export type Periodo = 'semana' | 'mes'
type Tipo = 'venda_direta' | 'parceiros' | 'todos'

interface AverageModel {
  count: number
  valorNewDriverSum: number
  periodsUsed: number
}

interface ComparativoResultModel {
  rows: PeriodoRowModel[]
  current: PeriodoRowModel | null
  average: AverageModel
  totalAverage: AverageModel
  serverTime: string
}

const WEEKS_SHOWN_BY_DEFAULT = 14
const MONTHS_SHOWN_BY_DEFAULT = 6

export const BiSemanalStore = createSimpleStore(
  {
    tipo: 'todos' as Tipo,
    periodo: 'semana' as Periodo,
    range: defaultRange('semana'),
    metric: 'valorNewDriverSum' as Metric,
    loading: false as boolean,
    rows: [] as PeriodoRowModel[],
    current: null as PeriodoRowModel | null,
    average: null as AverageModel | null,
    totalAverage: null as AverageModel | null,
    serverTime: null as string | null,
    error: null as string | null,
  },
  {
    setTipo: (state, tipo: Tipo) => ({ ...state, tipo }),
    setPeriodo: (state, periodo: Periodo) => ({ ...state, periodo, range: defaultRange(periodo) }),
    setRange: (state, range: [string, string]) => ({ ...state, range }),
    setMetric: (state, metric: Metric) => ({ ...state, metric }),
    fetchStarted: (state) => ({ ...state, loading: true, error: null }),
    fetchSuccess: (state, payload: ComparativoResultModel) => ({
      ...state,
      ...payload,
      loading: false,
    }),
    fetchFailed: (state, error: string) => ({ ...state, loading: false, error }),
  },
  {
    thunks: {
      fetchData({ tipo, periodo, range }: { tipo: Tipo; periodo: Periodo; range: [string, string] }) {
        return async (dispatch: any) => {
          dispatch(BiSemanalStore.actions.fetchStarted())
          try {
            const { data } = await Axios.get<ComparativoResultModel>(
              periodo === 'semana' ? '/matriculas/bi/semanal' : '/matriculas/bi/comparativo-mensal',
              { params: buildParams(tipo, periodo, range) }
            )
            dispatch(BiSemanalStore.actions.fetchSuccess(data))
          } catch {
            dispatch(BiSemanalStore.actions.fetchFailed('Erro ao carregar dados'))
          }
        }
      },
    },
  }
)

function defaultRange(periodo: Periodo): [string, string] {
  const today = dayjs()
  const start =
    periodo === 'semana'
      ? sundayOf(today).subtract(WEEKS_SHOWN_BY_DEFAULT, 'week')
      : today.startOf('month').subtract(MONTHS_SHOWN_BY_DEFAULT, 'month')
  return [start.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')]
}

function sundayOf(date: dayjs.Dayjs): dayjs.Dayjs {
  return date.subtract(date.day(), 'day').startOf('day')
}

function buildParams(tipo: Tipo, periodo: Periodo, range: [string, string]) {
  if (periodo === 'semana') return { tipo, inicio: range[0], fim: range[1] }
  return { tipo, inicio: range[0].slice(0, 7), fim: range[1].slice(0, 7) }
}

export function BiComparativoSemanal() {
  const init = useCallback((dispatch: any) => {
    dispatch(BiSemanalStore.thunks.fetchData({ tipo: 'todos', periodo: 'semana', range: defaultRange('semana') }))
  }, [])

  return (
    <BiSemanalStore.Provider init={init}>
      <Toolbar>
        <ModoToggle />
        <PeriodoToggle />
        <RangeControl />
      </Toolbar>
      <ComparativoSemanalContent />
    </BiSemanalStore.Provider>
  )
}

function ModoToggle() {
  const { tipo, periodo, range } = BiSemanalStore.useState()
  const dispatch = BiSemanalStore.useDispatch()

  function handleTipoChange(newTipo: Tipo) {
    dispatch(BiSemanalStore.actions.setTipo(newTipo))
    dispatch(BiSemanalStore.thunks.fetchData({ tipo: newTipo, periodo, range }))
  }

  return (
    <Radio.Group
      value={tipo}
      onChange={(e) => handleTipoChange(e.target.value)}
      optionType="button"
      buttonStyle="solid"
      options={[
        { label: 'Todos', value: 'todos' },
        { label: 'Venda Direta', value: 'venda_direta' },
        { label: 'Parceiros', value: 'parceiros' },
      ]}
    />
  )
}

function PeriodoToggle() {
  const { tipo, periodo } = BiSemanalStore.useState()
  const dispatch = BiSemanalStore.useDispatch()

  function handlePeriodoChange(newPeriodo: Periodo) {
    dispatch(BiSemanalStore.actions.setPeriodo(newPeriodo))
    dispatch(BiSemanalStore.thunks.fetchData({ tipo, periodo: newPeriodo, range: defaultRange(newPeriodo) }))
  }

  return (
    <Radio.Group
      value={periodo}
      onChange={(e) => handlePeriodoChange(e.target.value)}
      optionType="button"
      buttonStyle="solid"
      options={[
        { label: 'Semana', value: 'semana' },
        { label: 'Mês', value: 'mes' },
      ]}
    />
  )
}

function RangeControl() {
  const { tipo, periodo, range } = BiSemanalStore.useState()
  const dispatch = BiSemanalStore.useDispatch()

  function handleChange(dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) {
    if (!dates || !dates[0] || !dates[1]) return
    const start = periodo === 'semana' ? sundayOf(dates[0]) : dates[0].startOf('month')
    const newRange: [string, string] = [start.format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
    dispatch(BiSemanalStore.actions.setRange(newRange))
    dispatch(BiSemanalStore.thunks.fetchData({ tipo, periodo, range: newRange }))
  }

  return (
    <RangePicker
      picker={periodo === 'semana' ? 'week' : 'month'}
      value={[dayjs(range[0]), dayjs(range[1])]}
      onChange={handleChange}
      disabledDate={(d) => d.isAfter(dayjs(), 'day')}
      format={periodo === 'semana' ? 'DD/MM/YYYY' : 'MM/YYYY'}
      allowClear={false}
    />
  )
}

function MetricToggle() {
  const { metric } = BiSemanalStore.useState()
  const dispatch = BiSemanalStore.useDispatch()

  return (
    <Radio.Group
      value={metric}
      onChange={(e) => dispatch(BiSemanalStore.actions.setMetric(e.target.value))}
      optionType="button"
      options={[
        { label: 'Receita', value: 'valorNewDriverSum' },
        { label: 'Matrículas', value: 'count' },
      ]}
    />
  )
}

function ComparativoSemanalContent() {
  return (
    <ContentStack>
      <ComparativoWidget />
      <MetricToggle />
      <SemanalChart />
      <SemanalTable />
    </ContentStack>
  )
}

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
